import { useSyncExternalStore } from 'react';
import type { ProjectionInputData, ResolvedProjectionAssumptions, TenantSettings } from '@dairy/shared';
import type { ProjectionResult } from '@dairy/domain';
import { sessionStore } from '@/auth/session';
import type { ProjectionFormValues } from './projection-input-form';

export interface ProjectionDraft {
  tenantId: string;
  userId: string;
  name: string;
  input: ProjectionInputData;
  assumptions: ResolvedProjectionAssumptions;
  settings: TenantSettings;
  herdMaxPeakMl: number | null;
  form: ProjectionFormValues;
  results: { low: ProjectionResult; expected: ProjectionResult; high: ProjectionResult };
}
let draft: ProjectionDraft | null = null;
let stopWatching: (() => void) | undefined;
const listeners = new Set<() => void>();
const belongsToOwner = () => {
  const active = sessionStore.get();
  return (
    active.status === 'signedIn' &&
    active.profile?.role === 'OWNER' &&
    active.profile.tenantId === draft?.tenantId &&
    active.profile.userId === draft?.userId
  );
};
export function clearProjectionDraft() {
  draft = null;
  stopWatching?.();
  stopWatching = undefined;
  for (const listener of listeners) listener();
}
/** Private memory only: never put financial inputs in router history or browser storage. */
export function setProjectionDraft(value: ProjectionDraft) {
  const active = sessionStore.get();
  if (
    active.status !== 'signedIn' ||
    active.profile?.role !== 'OWNER' ||
    active.profile.tenantId !== value.tenantId ||
    active.profile.userId !== value.userId
  )
    throw new Error('Projection draft requires its owner');
  draft = structuredClone(value);
  stopWatching ??= sessionStore.subscribe(() => {
    if (!belongsToOwner()) clearProjectionDraft();
  });
  for (const listener of listeners) listener();
}
export function getProjectionDraft() {
  return belongsToOwner() ? draft : null;
}
export function useProjectionDraft() {
  return useSyncExternalStore((listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, getProjectionDraft);
}
